import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { DESTINATIONS, waLink } from "@/data/site";

const FILTERS = [
  { id: "all", label: "Todos", testid: "service-filter-all" },
  { id: "americas", label: "Américas", testid: "service-filter-americas" },
  { id: "asia", label: "Ásia & Oceania", testid: "service-filter-asia" },
  { id: "global", label: "Europa & Passaportes", testid: "service-filter-global" },
];

const spanFor = (d, filtered) => {
  if (filtered) return "";
  if (d.featured) return "lg:col-span-2 lg:row-span-2";
  if (d.wide) return "lg:col-span-3";
  return "";
};

export const Destinations = () => {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const filtered = DESTINATIONS.filter(
    (d) => filter === "all" || d.region === filter
  );

  return (
    <section id="destinos" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <p className="mb-3 font-mono-tech text-xs tracking-[0.3em] text-gold-400">
            01 — DESTINOS ATENDIDOS
          </p>
          <h2 className="font-serif-display max-w-xl text-3xl font-normal leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Do consulado de São Paulo ao mundo,{" "}
            <em className="gold-text italic">sem margem para erro.</em>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={f.testid}
              onClick={() => setFilter(f.id)}
              className={`rounded-full border px-4 py-2 font-mono-tech text-[11px] tracking-widest transition-colors duration-300 ${
                filter === f.id
                  ? "border-gold-400 bg-gold-400/15 text-gold-200"
                  : "border-slate-600/50 text-slate-400 hover:border-gold-400/40 hover:text-gold-300"
              }`}
            >
              {f.label.toUpperCase()}
            </button>
          ))}
        </div>
      </motion.div>

      <motion.div layout className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <AnimatePresence mode="popLayout">
          {filtered.map((d, i) => (
            <motion.article
              layout
              key={d.id}
              data-testid={`service-card-${d.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setSelected(d)}
              className={`card-rim group relative cursor-pointer overflow-hidden rounded-xl border border-gold-400/20 bg-navy-800 ${spanFor(d, filter !== "all")}`}
            >
              <div className={`relative overflow-hidden ${d.featured && filter === "all" ? "h-72 lg:h-full lg:min-h-[26rem]" : "h-56"}`}>
                <img
                  src={d.image}
                  alt={d.country}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/35 to-transparent" />
                <div className="absolute left-4 top-4 flex items-center gap-2">
                  <span className="rounded-full border border-gold-400/40 bg-navy-950/70 px-3 py-1 font-mono-tech text-[10px] tracking-widest text-gold-200 backdrop-blur-sm">
                    {d.tag.toUpperCase()}
                  </span>
                  {d.forte && (
                    <span className="rounded-full bg-gold-400 px-3 py-1 font-mono-tech text-[10px] font-semibold tracking-widest text-navy-950">
                      NOSSO FORTE
                    </span>
                  )}
                </div>
                <ArrowUpRight className="absolute right-4 top-4 h-5 w-5 text-gold-300 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif-display text-2xl text-slate-50">{d.country}</h3>
                <p className="mt-1 text-xs font-light text-slate-300">{d.category}</p>
                <div className="mt-3 flex items-center gap-4 font-mono-tech text-[10px] tracking-wider text-slate-400">
                  <span>
                    <span className="text-emerald-400">{d.rate}</span>{" "}
                    {d.rateUnit || "APROVAÇÃO"}
                  </span>
                  <span className="hidden sm:inline">{d.badge.toUpperCase()}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>

      <p className="mt-6 text-[11px] font-light leading-relaxed text-slate-500">
        *Índices históricos da carteira de clientes atendidos; resultados variam
        conforme o perfil de cada solicitante. A concessão de vistos é decisão
        exclusiva dos consulados e embaixadas.
      </p>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent
          data-testid="destination-detail-modal"
          className="max-w-lg border-gold-400/30 bg-navy-900 text-slate-100"
        >
          {selected && (
            <>
              <div className="relative -mx-6 -mt-6 mb-2 h-44 overflow-hidden rounded-t-lg">
                <img src={selected.image} alt={selected.country} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900 to-transparent" />
              </div>
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl text-slate-50">
                  {selected.country}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  {selected.category} • Aprovação {selected.rate} • {selected.time}
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-3 space-y-3">
                {selected.checklist.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm font-light text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                data-testid="destination-whatsapp-button"
                href={waLink(`Olá Melo Vistos! Tenho interesse no visto para ${selected.country}. Podem me ajudar?`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-gold-400 px-6 py-3 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-200"
              >
                <MessageCircle className="h-4 w-4" />
                Consultar {selected.country} no WhatsApp
              </a>
              <Link
                data-testid="destination-full-page-link"
                to={`/destinos/${selected.id}`}
                className="mt-3 flex items-center justify-center gap-2 rounded-full border border-slate-600/60 px-6 py-3 text-sm text-slate-200 transition-colors duration-300 hover:border-gold-400/60 hover:text-gold-200"
              >
                Ver documentos, prazos e taxas →
              </Link>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
